import { useEffect, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { preferencesApi } from '../../../api/client';
import type { UserPreferences } from '../../../api/client';
import { useTheme } from '../../../contexts/ThemeContext';

const THEMES = [
  { value: 'dark', label: 'Dark', description: 'Dark background, easy on the eyes' },
  { value: 'light', label: 'Light', description: 'Light background, clean and bright' },
];

const ODDS_FORMATS = [
  { value: 'american', label: 'American', example: '-110 / +150' },
  { value: 'decimal', label: 'Decimal', example: '1.91 / 2.50' },
  { value: 'fractional', label: 'Fractional', example: '10/11 / 3/2' },
];

const SPORTS = [
  { value: 'mlb', label: 'MLB Baseball' },
  { value: 'nfl', label: 'NFL Football' },
  { value: 'nba', label: 'NBA Basketball' },
  { value: 'nhl', label: 'NHL Hockey' },
];

const DASHBOARDS = [
  { value: 'slate', label: "Today's Slate" },
  { value: 'bankroll', label: 'Bankroll Overview' },
  { value: 'advisor', label: 'Bet Advisor' },
];

export function PreferencesSettingsPage() {
  const { setTheme } = useTheme();
  const [prefs, setPrefs] = useState<UserPreferences>({
    theme: 'dark',
    odds_format: 'american',
    preferred_sport: null,
    default_dashboard: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    setLoading(true);
    preferencesApi.get()
      .then(setPrefs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleUpdate(updates: Partial<UserPreferences>) {
    const prevPrefs = prefs;
    setPrefs((p) => ({ ...p, ...updates }));
    setSaving(true);
    setSaveStatus('idle');
    try {
      const updated = await preferencesApi.update(updates);
      setPrefs(updated);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: unknown) {
      setPrefs(prevPrefs); // revert to state captured before optimistic update
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Preferences</h2>
          <p className="text-sm text-gray-500 mt-1">Customize your Navyrx Sports experience.</p>
        </div>
        <div className="flex items-center gap-2 h-7">
          {saving && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1 text-xs text-win">
              <CheckCircle className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1 text-xs text-loss">
              <AlertCircle className="w-3.5 h-3.5" /> {saveError || 'Error'}
            </span>
          )}
        </div>
      </div>

      {/* Theme */}
      <div className="op-panel p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Theme</h3>
        <div className="grid grid-cols-2 gap-3">
          {THEMES.map(({ value, label, description }) => (
            <button
              key={value}
              onClick={() => {
                const t = value as 'dark' | 'light';
                // Apply directly to DOM immediately (belt-and-suspenders alongside ThemeContext)
                document.documentElement.classList.remove('dark', 'light');
                document.documentElement.classList.add(t);
                localStorage.setItem('theme', t);
                setTheme(t);
                handleUpdate({ theme: value });
              }}
              className={`text-left px-4 py-3 rounded-md border transition-colors ${
                prefs.theme === value
                  ? 'border-brand bg-brand/10 text-gray-100'
                  : 'border-surface-4 hover:border-surface-3 text-gray-400 hover:text-gray-300'
              }`}
            >
              <p className="text-sm font-medium">{label}</p>
              <p className="text-xs mt-0.5 opacity-70">{description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Odds Format */}
      <div className="op-panel p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Odds Format</h3>
        <div className="grid grid-cols-3 gap-3">
          {ODDS_FORMATS.map(({ value, label, example }) => (
            <button
              key={value}
              onClick={() => handleUpdate({ odds_format: value })}
              className={`text-left px-3 py-3 rounded-md border transition-colors ${
                prefs.odds_format === value
                  ? 'border-brand bg-brand/10 text-gray-100'
                  : 'border-surface-4 hover:border-surface-3 text-gray-400 hover:text-gray-300'
              }`}
            >
              <p className="text-sm font-medium">{label}</p>
              <p className="text-xs mt-1 font-mono opacity-60">{example}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Preferred Sport */}
      <div className="op-panel p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Preferred Sport</h3>
        <p className="text-xs text-gray-500">Sets the default sport for recommendations and analytics.</p>
        <div className="grid grid-cols-2 gap-2">
          {[{ value: '', label: 'No preference' }, ...SPORTS].map(({ value, label }) => (
            <button
              key={value || 'none'}
              onClick={() => handleUpdate({ preferred_sport: value || null })}
              className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                (prefs.preferred_sport ?? '') === value
                  ? 'border-brand bg-brand/10 text-gray-100'
                  : 'border-surface-4 hover:border-surface-3 text-gray-400 hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Default Dashboard */}
      <div className="op-panel p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Default Dashboard</h3>
        <p className="text-xs text-gray-500">The page shown after you sign in.</p>
        <div className="grid grid-cols-3 gap-2">
          {[{ value: '', label: 'Default' }, ...DASHBOARDS].map(({ value, label }) => (
            <button
              key={value || 'default'}
              onClick={() => handleUpdate({ default_dashboard: value || null })}
              className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                (prefs.default_dashboard ?? '') === value
                  ? 'border-brand bg-brand/10 text-gray-100'
                  : 'border-surface-4 hover:border-surface-3 text-gray-400 hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
